import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Boxes,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Maximize2,
  Minus,
  Play,
  Plus,
  RotateCcw,
  ShieldCheck,
  ShoppingBag,
  Star,
  Ticket,
  Truck,
  X,
  Zap,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { checkDelivery, formatApiError, getOffers, getProduct, getProductReviews, getProducts, getRecentSlugs, getSettings, inr, recordView, requestBuyNow } from "@/lib/api";

const Stars = ({ value, size = "w-4 h-4" }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
    ))}
  </span>
);

const OFFER_ICONS = { coupon: Ticket, shipping: Truck, bulk: Boxes, business: Building2 };

const youtubeEmbed = (url) => {
  const m = url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const inputCls =
  "rounded-md border border-input bg-white/70 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

export default function ProductPage() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0, distribution: {} });
  const [offers, setOffers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [variant, setVariant] = useState(null);
  const [pack, setPack] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [mediaTab, setMediaTab] = useState("photos");
  const [pin, setPin] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [freeShipAbove, setFreeShipAbove] = useState(499);

  useEffect(() => {
    let alive = true;
    setProduct(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    Promise.all([getProduct(slug), getProducts(), getProductReviews(slug), getOffers(), getSettings().catch(() => null)])
      .then(([p, all, rev, off, st]) => {
        if (st) setFreeShipAbove(st.free_shipping_above);
        if (!alive) return;
        setProduct(p);
        setAllProducts(all);
        setReviews(rev);
        setOffers(off);
        setVariant(p.variants?.[0] || null);
        setPack(null);
        setQty(1);
        setActiveImg(0);
        recordView(p.slug);
        document.title = `${p.name} | Classina Products`;
      })
      .catch(() => alive && setNotFound(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = [product.image, ...(product.images || [])];
    const variantImgs = (product.variants || []).map((v) => v.image).filter(Boolean);
    const allImgs = variant?.image ? [variant.image, ...imgs, ...variantImgs] : [...imgs, ...variantImgs];
    return [...new Set(allImgs.filter(Boolean))];
  }, [product, variant]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (mediaTab === "photos" && gallery.length > 1) {
        if (e.key === "ArrowRight") setActiveImg((i) => (i + 1) % gallery.length);
        if (e.key === "ArrowLeft") setActiveImg((i) => (i - 1 + gallery.length) % gallery.length);
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, mediaTab, gallery.length]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="pdp-not-found">
          <h1 className="font-serif text-3xl font-semibold">Product not found</h1>
          <Link to="/" className="inline-block mt-6 rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-semibold">
            Back to store
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 aspect-square rounded-2xl bg-muted/50 animate-pulse" />
          <div className="lg:col-span-5 space-y-4 pt-8">
            <div className="h-8 w-2/3 rounded bg-muted/50 animate-pulse" />
            <div className="h-5 w-1/3 rounded bg-muted/50 animate-pulse" />
            <div className="h-10 w-1/4 rounded bg-muted/50 animate-pulse" />
          </div>
          <div className="lg:col-span-3 h-80 rounded-2xl bg-muted/50 animate-pulse" />
        </div>
      </div>
    );
  }

  const packs = product.packs || [];
  const stock = product.stock ?? 100;
  const outOfStock = stock <= 0;
  const lowStock = !outOfStock && stock <= 20;
  const unitPrice = pack ? pack.price : product.price;
  const compareAt = pack ? product.price * pack.pieces : product.mrp;
  const discountPct = compareAt > unitPrice ? Math.round((1 - unitPrice / compareAt) * 100) : 0;
  const sku = product.sku || `CLS-${product.slug.toUpperCase().replace(/-/g, "")}`;
  const piecesPerUnit = pack ? pack.pieces : 1;
  const totalPieces = qty * piecesPerUnit;
  const attrs = product.attributes || {};
  const highlights = { Brand: "Classina", ...attrs, ...(attrs["Country of Origin"] ? {} : { "Country of Origin": "India" }) };
  const highlightEntries = Object.entries(highlights).slice(0, 8);
  const specRows = { Brand: "Classina", Manufacturer: "SBK Manufacturing Co., Pune", SKU: sku, "Pack Size": pack ? pack.label : "Single pc", ...attrs, ...(attrs["Country of Origin"] ? {} : { "Country of Origin": "India" }) };

  const image = gallery[activeImg] || product.image;
  const ytEmbed = youtubeEmbed(product.video);
  const step = (d) => setActiveImg((i) => (i + d + gallery.length) % gallery.length);

  const etaDates = (eta) => {
    const [lo, hi] = eta.split("–").map(Number);
    const fmt = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    const a = new Date(Date.now() + lo * 86400000);
    const b = new Date(Date.now() + hi * 86400000);
    return `${fmt(a)} – ${fmt(b)}`;
  };

  const checkPin = async () => {
    setPinBusy(true);
    try {
      setPinResult(await checkDelivery(pin));
    } catch (err) {
      setPinResult({ error: formatApiError(err.response?.data?.detail) });
    } finally {
      setPinBusy(false);
    }
  };

  const addToCart = () => {
    add(product, qty, variant, pack);
  };

  const buyNow = () => {
    add(product, qty, variant, pack);
    requestBuyNow();
  };

  const related = allProducts.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 8);
  const alsoViewed = getRecentSlugs().filter((s) => s !== product.slug).map((s) => allProducts.find((p) => p.slug === s)).filter(Boolean).slice(0, 8);
  const maxDist = Math.max(1, ...Object.values(reviews.distribution || {}));

  const packOptions = [
    { key: "single", label: "Pack of 1", pieces: 1, price: product.price, savePct: 0, value: null },
    ...packs.map((p) => {
      const singlesTotal = product.price * p.pieces;
      return {
        key: `pack-${p.pieces}`,
        label: `Pack of ${p.pieces}`,
        pieces: p.pieces,
        price: p.price,
        savePct: singlesTotal > p.price ? Math.round((1 - p.price / singlesTotal) * 100) : 0,
        value: p,
      };
    }),
  ];

  const Rail = ({ title, items, testid }) =>
    items.length > 0 && (
      <section className="mt-16" data-testid={testid}>
        <h2 className="font-serif text-2xl md:text-3xl font-medium tracking-tight">{title}</h2>
        <div className="mt-6 flex gap-4 overflow-x-auto pb-4 -mx-6 px-6 lg:mx-0 lg:px-0">
          {items.map((p) => (
            <Link
              key={p.slug}
              to={`/product/${p.slug}`}
              data-testid={`${testid}-${p.slug}`}
              className="shrink-0 w-48 bg-white/70 border border-border/60 rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-primary/30 transition-all duration-300"
            >
              <img src={p.image} alt={p.name} className="w-full h-40 object-contain" />
              <div className="p-3.5">
                <p className="font-serif text-sm font-medium leading-snug line-clamp-2 min-h-10">{p.name}</p>
                {p.rating_count > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-[11px] font-bold text-amber-600">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {p.rating_average} ({p.rating_count})
                  </p>
                )}
                <p className="text-sm font-bold mt-1.5">{inr(p.price)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    );

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-[90rem] mx-auto px-6 md:px-10 lg:px-12 py-8 md:py-10" data-testid="pdp">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-7" data-testid="pdp-breadcrumb">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/?cat=${encodeURIComponent(product.category)}#shop`} className="hover:text-foreground transition-colors">{product.category}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground font-medium truncate max-w-48">{product.name}</span>
        </nav>

        {/* Amazon 3-column layout: gallery | details | buy box */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* LEFT — sticky gallery with vertical thumbnails */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="flex gap-3">
                {(gallery.length > 1 || product.video) && (
                  <div className="hidden md:flex flex-col gap-2.5 shrink-0" data-testid="pdp-thumbs">
                    {gallery.map((img, i) => (
                      <button
                        key={i}
                        data-testid={`pdp-thumb-${i}`}
                        onMouseEnter={() => setActiveImg(i)}
                        onClick={() => setActiveImg(i)}
                        className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                          activeImg === i ? "border-primary shadow-md" : "border-transparent opacity-60 hover:opacity-100"
                        }`}
                        aria-label={`Photo ${i + 1}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-contain" />
                      </button>
                    ))}
                    {product.video && (
                      <button
                        data-testid="pdp-thumb-video"
                        onClick={() => {
                          setMediaTab("video");
                          setLightbox(true);
                        }}
                        className="w-14 h-14 rounded-lg border-2 border-transparent opacity-70 hover:opacity-100 bg-black text-white flex flex-col items-center justify-center gap-0.5 shrink-0 transition-all"
                        aria-label="Play product video"
                      >
                        <Play className="w-4 h-4 fill-white" />
                        <span className="text-[8px] font-bold tracking-wider">VIDEO</span>
                      </button>
                    )}
                  </div>
                )}
                <div
                  className="relative flex-1 rounded-2xl overflow-hidden bg-white border border-border/60 aspect-square cursor-zoom-in"
                  data-testid="pdp-main-image"
                  onClick={() => {
                    setMediaTab("photos");
                    setLightbox(true);
                  }}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
                  }}
                  onMouseLeave={() => setZoom(null)}
                >
                  <img
                    src={image}
                    alt={product.name}
                    className="w-full h-full object-contain transition-transform duration-200"
                    style={zoom ? { transform: "scale(1.9)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined}
                  />
                  {product.badge && (
                    <span className="absolute top-4 left-4 bg-accent text-accent-foreground text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1 rounded-full">
                      {product.badge}
                    </span>
                  )}
                  {discountPct > 0 && (
                    <span data-testid="pdp-discount-badge" className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full">
                      {discountPct}% OFF
                    </span>
                  )}
                  <span className="absolute bottom-4 right-4 rounded-full bg-black/55 backdrop-blur-sm text-white p-2.5 opacity-80 hover:opacity-100 transition-opacity" title="Click to preview">
                    <Maximize2 className="w-4 h-4" />
                  </span>
                </div>
              </div>
              {(gallery.length > 1 || product.video) && (
                <div className="flex md:hidden gap-2 overflow-x-auto mt-3" data-testid="pdp-thumbs-mobile">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 shrink-0 ${activeImg === i ? "border-primary" : "border-transparent opacity-60"}`}
                      aria-label={`Photo ${i + 1}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-contain" />
                    </button>
                  ))}
                  {product.video && (
                    <button
                      data-testid="pdp-thumb-video-mobile"
                      onClick={() => {
                        setMediaTab("video");
                        setLightbox(true);
                      }}
                      className="w-14 h-14 rounded-lg border-2 border-transparent bg-black text-white flex flex-col items-center justify-center gap-0.5 shrink-0"
                      aria-label="Play product video"
                    >
                      <Play className="w-4 h-4 fill-white" />
                      <span className="text-[8px] font-bold tracking-wider">VIDEO</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CENTER — title, ratings, price, variants, highlights, offers */}
          <div className="lg:col-span-5">
            <h1 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight leading-tight" data-testid="pdp-title">
              {product.name}{variant ? ` | ${variant.name}` : ""}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
              {reviews.count > 0 ? (
                <span className="flex items-center gap-1.5 font-bold text-amber-600" data-testid="pdp-rating">
                  <Stars value={reviews.average} /> {reviews.average} · {reviews.count} rating{reviews.count > 1 ? "s" : ""}
                </span>
              ) : (
                <span className="text-muted-foreground">No reviews yet</span>
              )}
              <span className="text-border">|</span>
              <span className="text-muted-foreground font-mono" data-testid="pdp-sku">SKU: {sku}</span>
              <span className="text-border">|</span>
              <span data-testid="pdp-stock-status" className={`font-bold flex items-center gap-1 ${outOfStock ? "text-accent" : "text-primary"}`}>
                <Check className="w-3.5 h-3.5" /> {outOfStock ? "Stock coming soon" : lowStock ? `Only ${stock} pcs left` : "In stock"}
              </span>
            </div>

            <hr className="my-5 border-border/60" />

            <div className="flex items-baseline gap-3 flex-wrap">
              {discountPct > 0 && (
                <span className="font-serif text-3xl font-light text-accent" data-testid="pdp-discount">-{discountPct}%</span>
              )}
              <span className="font-serif text-3xl md:text-4xl font-bold" data-testid="pdp-price">{inr(unitPrice)}</span>
              {compareAt > unitPrice && (
                <span className="text-sm text-muted-foreground" >
                  MRP: <span className="line-through" data-testid="pdp-mrp">{inr(compareAt)}</span>
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes{pack ? ` · ${inr(Math.round(pack.price / pack.pieces))} per pc` : ""}</p>

            {product.variants?.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">
                  Type: <span className="text-foreground" data-testid="pdp-variant-name">{variant?.name}</span>
                </p>
                <div className="flex flex-wrap gap-2" data-testid="pdp-variants">
                  {product.variants.map((v) => (
                    <button
                      key={v.name}
                      data-testid={`pdp-variant-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setVariant(v)}
                      className={`rounded-full px-4 py-2 text-xs font-bold border transition-all duration-300 ${
                        variant?.name === v.name
                          ? "bg-primary text-primary-foreground border-primary shadow-md"
                          : "bg-white/60 border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                      }`}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {offers.length > 0 && (
              <div className="mt-6" data-testid="pdp-offers">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">Offers</p>
                <ul className="space-y-2">
                  {offers.map((o, i) => {
                    const Icon = OFFER_ICONS[o.kind] || Ticket;
                    return (
                      <li key={i} className="text-xs flex items-start gap-2">
                        <Icon className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                        <span>{o.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="mt-6 rounded-xl border border-border/60 bg-secondary/30 p-4" data-testid="pdp-highlights">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">Highlights</p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {highlightEntries.map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <dt className="text-muted-foreground inline">{k}: </dt>
                    <dd className="font-semibold inline">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {(product.specs || []).length > 0 && (
              <div className="mt-6" data-testid="pdp-about-inline">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">About this item</p>
                <ul className="space-y-2">
                  {product.specs.map((s) => (
                    <li key={s} className="text-sm flex items-start gap-2.5">
                      <span className="text-primary mt-0.5">✦</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* RIGHT — sticky Buy Box */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 rounded-2xl border border-border/70 bg-white/80 backdrop-blur-sm shadow-md p-5" data-testid="pdp-buy-box">
              <p className="font-serif text-2xl font-bold" data-testid="pdp-buy-price">{inr(unitPrice)}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {pack ? `${pack.label} · ${inr(Math.round(pack.price / pack.pieces))} per pc` : "Pack of 1"}
              </p>

              <div className="mt-3 space-y-1 text-xs">
                <p className="flex items-center gap-1.5 text-foreground font-semibold">
                  <Truck className="w-3.5 h-3.5 text-primary" />
                  {unitPrice * qty >= freeShipAbove ? "FREE delivery" : `Free delivery above ₹${freeShipAbove.toLocaleString("en-IN")}`}
                </p>
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <Check className="w-3.5 h-3.5 text-primary" /> COD available · Ships from Pune in 24–48 hrs
                </p>
              </div>

              {packs.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-2">Pack size</p>
                  <div className="space-y-1.5" data-testid="pdp-packs">
                    {packOptions.map((opt) => {
                      const active = opt.value === null ? !pack : pack?.label === opt.value.label;
                      return (
                        <button
                          key={opt.key}
                          data-testid={opt.value === null ? "pdp-pack-single" : `pdp-pack-${opt.pieces}`}
                          onClick={() => setPack(opt.value)}
                          className={`w-full rounded-lg border px-3 py-2.5 flex items-center justify-between gap-2 text-left transition-all duration-300 ${
                            active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white/50 hover:border-primary/40"
                          }`}
                        >
                          <span>
                            <span className={`block text-xs font-bold ${active ? "text-foreground" : "text-foreground"}`}>{opt.label}</span>
                            <span className="block text-[11px] text-muted-foreground">{inr(opt.price)}{opt.pieces > 1 ? ` · ${inr(Math.round(opt.price / opt.pieces))}/pc` : " / pc"}</span>
                          </span>
                          {opt.savePct > 0 && (
                            <span className="shrink-0 bg-accent text-accent-foreground text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                              Save {opt.savePct}%
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center gap-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Qty</p>
                <div className="flex items-center border border-border rounded-full bg-white/60">
                  <button data-testid="pdp-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2 hover:text-primary transition-colors" aria-label="Decrease quantity">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span data-testid="pdp-qty" role="spinbutton" aria-valuenow={qty} aria-label="Quantity" className="w-9 text-center text-sm font-bold">{qty}</span>
                  <button data-testid="pdp-qty-plus" onClick={() => setQty((q) => Math.min(Math.max(stock, 1), q + 1))} className="p-2 hover:text-primary transition-colors" aria-label="Increase quantity">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground" data-testid="pdp-pieces-math">
                  {pack ? `= ${totalPieces} pcs` : ""}
                </p>
              </div>

              {product.min_qty > 1 && (
                <p className="mt-3 text-[11px] font-semibold text-accent bg-accent/10 rounded-lg px-3 py-2" data-testid="pdp-min-set">
                  Sold in sets — minimum {product.min_qty} pcs (mix options freely)
                </p>
              )}

              <div className="mt-4 space-y-2.5">
                {outOfStock ? (
                  <div data-testid="pdp-out-of-stock" className="rounded-xl bg-accent/10 border border-accent/30 px-4 py-3.5 text-center">
                    <p className="text-sm font-bold text-accent">Stock coming soon</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">We're making more of this — please check back shortly</p>
                  </div>
                ) : (
                  <>
                    <button
                      data-testid="pdp-add-to-cart"
                      onClick={addToCart}
                      className="w-full rounded-full border-2 border-primary text-primary py-3 text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary/5 transition-all duration-300"
                    >
                      <ShoppingBag className="w-4 h-4" /> Add to cart
                    </button>
                    <button
                      data-testid="pdp-buy-now"
                      onClick={buyNow}
                      className="w-full rounded-full bg-primary text-primary-foreground py-3 text-sm font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                    >
                      <Zap className="w-4 h-4" /> Buy now
                    </button>
                  </>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-border/60" data-testid="pdp-delivery">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-2 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-primary" /> Deliver to
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="pdp-pin-input"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="PIN code"
                    inputMode="numeric"
                    className={`${inputCls} flex-1 min-w-0`}
                  />
                  <button
                    data-testid="pdp-pin-check"
                    onClick={checkPin}
                    disabled={pinBusy || pin.length !== 6}
                    className="rounded-md bg-secondary px-4 py-2.5 text-xs font-bold hover:brightness-95 transition disabled:opacity-50 shrink-0"
                  >
                    {pinBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Check"}
                  </button>
                </div>
                {pinResult && (
                  pinResult.error ? (
                    <p data-testid="pdp-pin-result" className="mt-2 text-[11px] font-semibold text-destructive">{pinResult.error}</p>
                  ) : (
                    <p data-testid="pdp-pin-result" className="mt-2 text-[11px] font-semibold text-primary">
                      Delivering to {pinResult.pincode} · {etaDates(pinResult.eta_days)}
                    </p>
                  )
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-border/60 space-y-1.5 text-[11px] text-muted-foreground">
                <p className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-primary" /> Secure transaction</p>
                <p className="flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5 text-primary" /> 7-day replacement on manufacturing defects</p>
              </div>
            </div>
          </aside>
        </div>

        {/* About + description */}
        <section className="mt-16 grid lg:grid-cols-2 gap-10" data-testid="pdp-about">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl font-medium tracking-tight">About this product</h2>
            <ul className="mt-5 space-y-2.5">
              {(product.specs || []).map((s) => (
                <li key={s} className="text-sm flex items-start gap-2.5">
                  <span className="text-primary mt-0.5">✦</span> {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl md:text-3xl font-medium tracking-tight">Product description</h2>
            <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{product.description}</p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Manufactured by SBK Manufacturing Co. in Pune — trusted by 150+ schools and 1,500+ retail outlets across India.
            </p>
          </div>
        </section>

        {/* Spec table */}
        <section className="mt-14" data-testid="pdp-spec-table">
          <h2 className="font-serif text-2xl md:text-3xl font-medium tracking-tight">Specifications</h2>
          <div className="mt-6 rounded-2xl border border-border/60 overflow-hidden bg-white/50">
            {Object.entries(specRows).map(([k, v], i) => (
              <div key={k} className={`grid grid-cols-2 text-sm ${i % 2 ? "bg-secondary/30" : ""}`}>
                <p className="px-5 py-3 text-muted-foreground font-medium">{k}</p>
                <p className="px-5 py-3 font-semibold">{v}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="mt-14" data-testid="pdp-reviews">
          <h2 className="font-serif text-2xl md:text-3xl font-medium tracking-tight">Customer ratings & reviews</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-8">
            <div className="bg-white/50 border border-border/60 rounded-2xl p-6 h-fit">
              <p className="font-serif text-5xl font-semibold" data-testid="pdp-reviews-average">{reviews.average || "—"}</p>
              <div className="mt-2"><Stars value={reviews.average} size="w-5 h-5" /></div>
              <p className="text-xs text-muted-foreground mt-2">{reviews.count} verified review{reviews.count === 1 ? "" : "s"}</p>
              <div className="mt-4 space-y-1.5">
                {[5, 4, 3, 2, 1].map((n) => (
                  <div key={n} className="flex items-center gap-2 text-xs">
                    <span className="w-3 font-bold">{n}</span>
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${((reviews.distribution?.[String(n)] || 0) / maxDist) * 100}%` }} />
                    </div>
                    <span className="w-6 text-muted-foreground text-right">{reviews.distribution?.[String(n)] || 0}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-4">Reviews are collected from verified delivered orders and approved by our team.</p>
            </div>
            <div className="md:col-span-2 space-y-4">
              {reviews.reviews.length === 0 && (
                <p className="text-sm text-muted-foreground bg-white/50 border border-border/60 rounded-2xl p-6">
                  No reviews yet. Delivered this product? Rate it from the Track Order page.
                </p>
              )}
              {reviews.reviews.map((r) => (
                <div key={r.id} className="bg-white/50 border border-border/60 rounded-2xl p-5" data-testid={`pdp-review-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-sm">{r.name}</p>
                    <Stars value={r.rating} />
                  </div>
                  {r.title && <p className="font-semibold text-sm mt-2">{r.title}</p>}
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.text}</p>
                  <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-primary" /> Verified purchase · {new Date(r.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Rail title="You may also like" items={related} testid="pdp-related" />
        <Rail title="Customers also viewed" items={alsoViewed} testid="pdp-also-viewed" />
      </main>

      {/* Fullscreen media preview (photos + video) */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setLightbox(false)}
            data-testid="pdp-lightbox"
          >
            <button
              data-testid="pdp-lightbox-close"
              onClick={() => setLightbox(false)}
              className="absolute top-5 right-5 z-10 rounded-full bg-white/10 border border-white/20 text-white p-2.5 hover:bg-white/20 transition-colors"
              aria-label="Close preview"
            >
              <X className="w-5 h-5" />
            </button>

            {mediaTab === "photos" && (
              <span className="absolute top-7 left-6 text-white/70 text-xs font-semibold tracking-widest" data-testid="pdp-lightbox-counter">
                {activeImg + 1} / {gallery.length}
              </span>
            )}

            {/* Floating photos/video toggle */}
            {product.video && (
              <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 flex gap-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md p-1 shadow-2xl" data-testid="pdp-lightbox-toggle">
                <button
                  data-testid="pdp-lightbox-photos-tab"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMediaTab("photos");
                  }}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                    mediaTab === "photos" ? "bg-white text-black" : "text-white/70 hover:text-white"
                  }`}
                >
                  <ImageIcon className="w-3.5 h-3.5" /> Photos
                </button>
                <button
                  data-testid="pdp-lightbox-video-tab"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMediaTab("video");
                  }}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                    mediaTab === "video" ? "bg-white text-black" : "text-white/70 hover:text-white"
                  }`}
                >
                  <Play className="w-3.5 h-3.5" /> Video
                </button>
              </div>
            )}

            {mediaTab === "photos" && gallery.length > 1 && (
              <>
                <button
                  data-testid="pdp-lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(-1);
                  }}
                  className="absolute left-4 md:left-8 z-10 rounded-full bg-white/10 border border-white/20 text-white p-3 hover:bg-white/20 transition-colors"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  data-testid="pdp-lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(1);
                  }}
                  className="absolute right-4 md:right-8 z-10 rounded-full bg-white/10 border border-white/20 text-white p-3 hover:bg-white/20 transition-colors"
                  aria-label="Next photo"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            <motion.div
              key={mediaTab === "video" ? "video" : image}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className={`flex items-center justify-center ${mediaTab === "photos" ? "bg-white rounded-2xl p-4 md:p-8 shadow-2xl" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              {mediaTab === "video" && product.video ? (
                ytEmbed ? (
                  <iframe
                    src={`${ytEmbed}?autoplay=1&rel=0`}
                    title="Product video"
                    data-testid="pdp-lightbox-video"
                    className="w-[88vw] max-w-4xl aspect-video rounded-xl border border-white/20"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={product.video}
                    controls
                    autoPlay
                    data-testid="pdp-lightbox-video"
                    className="max-w-[88vw] max-h-[78vh] rounded-xl"
                  />
                )
              ) : (
                <img
                  src={image}
                  alt={product.name}
                  data-testid="pdp-lightbox-image"
                  className="max-w-[82vw] max-h-[68vh] object-contain"
                />
              )}
            </motion.div>

            {mediaTab === "photos" && gallery.length > 1 && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-2 py-1" data-testid="pdp-lightbox-thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveImg(i);
                    }}
                    className={`w-12 h-12 rounded-lg overflow-hidden border-2 shrink-0 bg-white/90 transition-all ${
                      activeImg === i ? "border-white" : "border-transparent opacity-50 hover:opacity-90"
                    }`}
                    aria-label={`Photo ${i + 1}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile sticky buy bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border/60 p-3 flex items-center gap-3" data-testid="pdp-mobile-bar">
        <div className="min-w-0">
          <p className="font-serif text-lg font-bold leading-none">{inr(unitPrice)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{pack ? pack.label : "Pack of 1"}</p>
        </div>
        {outOfStock ? (
          <p data-testid="pdp-mobile-oos" className="flex-1 text-center text-xs font-bold text-accent py-3">Stock coming soon</p>
        ) : (
          <>
            <button
              data-testid="pdp-mobile-add"
              onClick={addToCart}
              className="flex-1 rounded-full border-2 border-primary text-primary py-3 text-xs font-bold"
            >
              Add to cart
            </button>
            <button
              data-testid="pdp-mobile-buy"
              onClick={buyNow}
              className="flex-1 rounded-full bg-primary text-primary-foreground py-3 text-xs font-bold"
            >
              Buy now
            </button>
          </>
        )}
      </div>
      <div className="lg:hidden h-20" />

      <Footer />
    </div>
  );
}
